import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download, FileDown } from "lucide-react";
import { toast } from "sonner";

function isoDay(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReportsPage() {
  const [start, setStart] = useState(isoDay(-29));
  const [end, setEnd] = useState(isoDay(0));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(currentMonthValue());
  const [monthDownloading, setMonthDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/summary", { params: { start_date: start, end_date: end } });
      setRows(data.rows || []);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const header = ["Employee", "Days present", "Total hours", "Hourly wage", "Estimated pay"];
    const csv = [
      header.join(","),
      ...rows.map((r) =>
        [r.employee_name, r.days_present, r.total_hours, r.hourly_wage, r.estimated_pay]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `royal-wow-report-${start}-to-${end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadMonth = async () => {
    if (!month) return;
    const [y, m] = month.split("-").map((v) => parseInt(v, 10));
    setMonthDownloading(true);
    try {
      const res = await api.get("/reports/monthly-sheet", {
        params: { year: y, month: m },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-attendance-${month}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${month} sheet`);
    } catch (err) {
      toast.error("Failed to download monthly sheet");
    } finally {
      setMonthDownloading(false);
    }
  };

  const totals = rows.reduce(
    (acc, r) => ({
      hours: acc.hours + r.total_hours,
      pay: acc.pay + r.estimated_pay,
    }),
    { hours: 0, pay: 0 }
  );

  return (
    <div data-testid="reports-page">
      <div className="fade-up flex items-end justify-between">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Insight</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Reports</h1>
          <p className="mt-3 text-muted-foreground">Hours worked and estimated pay per employee.</p>
        </div>
        <Button
          onClick={exportCsv}
          data-testid="export-csv-btn"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Download className="w-4 h-4 mr-2" aria-hidden="true" /> Export range CSV
        </Button>
      </div>

      <div className="mt-8 bg-white border border-border p-6 fade-up flex flex-col md:flex-row md:items-end gap-4 md:gap-6" data-testid="monthly-sheet-panel">
        <div className="md:flex-1">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Monthly sheet</p>
          <h2 className="mt-2 font-serif text-2xl tracking-tight">Full month attendance</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            One CSV with every employee across every day of the month, plus totals and estimated pay.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Month</Label>
          <Input
            type="month"
            data-testid="month-picker"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="mt-2"
          />
        </div>
        <Button
          onClick={downloadMonth}
          disabled={monthDownloading || !month}
          data-testid="download-month-btn"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <FileDown className="w-4 h-4 mr-2" aria-hidden="true" />
          {monthDownloading ? "Preparing…" : "Download month sheet"}
        </Button>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 bg-white border border-border p-6 fade-up">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
          <Input type="date" data-testid="report-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
          <Input type="date" data-testid="report-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-2" />
        </div>
        <div className="flex flex-col justify-end text-right">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Estimated total pay</div>
          <div className="font-serif text-3xl mt-1" data-testid="total-pay">₹{totals.pay.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground">{totals.hours.toFixed(2)} hours across {rows.length} staff</div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-border fade-up">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No data for this range.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[620px]" data-testid="report-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-6 py-4 font-normal">Employee</th>
                <th className="px-6 py-4 font-normal text-right">Days present</th>
                <th className="px-6 py-4 font-normal text-right">Total hours</th>
                <th className="px-6 py-4 font-normal text-right">Wage / hr</th>
                <th className="px-6 py-4 font-normal text-right">Estimated pay</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-border">
                  <td className="px-6 py-4 font-medium">{r.employee_name}</td>
                  <td className="px-6 py-4 text-right">{r.days_present}</td>
                  <td className="px-6 py-4 text-right font-serif text-lg">{r.total_hours}</td>
                  <td className="px-6 py-4 text-right text-muted-foreground">₹{r.hourly_wage.toFixed(2)}</td>
                  <td className="px-6 py-4 text-right font-serif text-lg text-primary">₹{r.estimated_pay.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
